import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { concat, concatMap, defer, from, of, takeWhile } from 'rxjs';
import {
  ClientRegisteredVendor,
  Company,
  CompanyVendor,
  ExemptVendor
} from 'src/app/classes/models';
import { ConfirmArchiveVendorModalComponent } from 'src/app/modals/confirm-archive-vendor-modal/confirm-archive-vendor-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ApVendorUtilityService } from 'src/app/services/utility/ap-vendor-utility.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';

@Component({
  selector: 'app-admin-compliance-exempt-vendor',
  templateUrl: './admin-compliance-exempt-vendor.component.html',
  styleUrl: './admin-compliance-exempt-vendor.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminComplianceExemptVendorComponent {
  constructor(
    private CompanyVendorService: CompanyVendorService,
    private UtilityService: UtilityService,
    private route: ActivatedRoute,
    private ToastService: ToastService,
    private Router: Router,
    private ModalService: NgbModal,
    private ExemptVendorService: ExemptVendorService,
    private ApVendorUtilityService: ApVendorUtilityService,
    private cdr: ChangeDetectorRef
  ) {
    this.loadCompanyVendor();
  }

  CompanyVendor?: CompanyVendor;
  ExemptVendor?: ExemptVendor;
  Company?: Company;

  loadCompanyVendor() {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = Number(paramMap.get('CompanyId'));
        const ExemptVendorId = Number(paramMap.get('ExemptVendorId'));

        if (!CompanyId || !ExemptVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing CompanyVendor info.',
            { returnError: true }
          );
        }

        this.CompanyVendorService.getCompanyVendor(
          this.UtilityService.stringifyObjectsInQueryObject({
            CompanyId,
            ExemptVendorId,
            isArchived: { $in: [true, false] },
            ExemptVendor: {
              Contact: {}
            },
            Company: {
              excludedAttributes: ['logo']
            }
          })
        ).subscribe({
          next: (CompanyVendor: CompanyVendor) => {
            this.CompanyVendor = CompanyVendor;

            if (!this.CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing CompanyVendor.',
                { returnError: true }
              );
            }

            const { ExemptVendor, Company } = this.CompanyVendor;

            if (ExemptVendor) {
              this.ExemptVendor = ExemptVendor;
            }

            if (Company) {
              this.Company = Company;
            }

            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  addEditExemptVendor() {
    this.Router.navigate(['overview'], { relativeTo: this.route });
  }

  viewDocumentsAndNotes() {
    this.Router.navigate(['documents-notes'], { relativeTo: this.route });
  }

  updateEINAutoLink() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update EIN Auto link. Missing CompanyVendor record.',
        { returnError: true }
      );
    }

    const { CompanyId, ExemptVendorId, allowEinAutoLink } = this.CompanyVendor;

    if (!CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing client id.', {
        returnError: true
      });
    }

    if (!ExemptVendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing vendor id.', {
        returnError: true
      });
    }

    this.ApVendorUtilityService.handleApAutolinkDisableModal(
      !!allowEinAutoLink,
      { CompanyId, ExemptVendorId }
    ).subscribe({
      next: () => {
        this.CompanyVendor!.allowEinAutoLink =
          !this.CompanyVendor!.allowEinAutoLink;

        from(
          companyVendorsService.updateOneById({
            pathParams: { companyVendorId: this.CompanyVendor!.id! },
            data: { allowEinAutoLink: this.CompanyVendor!.allowEinAutoLink }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully updated EIN Auto link.'
            );
            this.loadCompanyVendor();
            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  convertExemptVendorToClientRegistered() {
    if (!this.ExemptVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor information.',
        { returnError: true }
      );
    }

    const { id: ExemptVendorId, Contacts } = this.ExemptVendor;

    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing client/vendor information.',
        { returnError: true }
      );
    }

    const { id: CompanyVendorId } = this.CompanyVendor;

    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing client information.',
        { returnError: true }
      );
    }

    const { id: CompanyId } = this.Company;

    const bvOrNonVerified = this.Company
      ?.allowAutomatedBusinessVerificationClientRegisteredVendors
      ? 'Business Verified'
      : 'Non-Verified';
    const observableArr = [];

    observableArr.push(
      defer(() => {
        const modalRef = this.ModalService.open(ConfirmModalComponent, {
          backdrop: 'static',
          centered: true,
          size: 'lg'
        });
        modalRef.componentInstance.displayText = `Are you sure you want to convert this vendor from Compliance Exempt to ${bvOrNonVerified}?`;

        return modalRef.closed;
      })
    );

    if (
      !Contacts ||
      Contacts.length === 0 ||
      !Contacts.find((contact) => contact.isPrimaryCompliance && contact.email)
    ) {
      observableArr.push(
        defer(() => {
          const modalRef = this.ModalService.open(ConfirmModalComponent, {
            backdrop: 'static',
            centered: true,
            size: 'lg'
          });
          modalRef.componentInstance.displayText =
            'You are converting vendor with no Contacts. No invitation e-mail will be sent to vendor without a contact with e-mail information. Do you want to continue?';

          return modalRef.closed;
        })
      );
    }

    observableArr.push(
      defer(() => {
        return this.ExemptVendorService.convertVendorToClientRegisteredVendor({
          ExemptVendorId,
          CompanyId
        }).pipe(
          concatMap((ClientRegisteredVendor: ClientRegisteredVendor) => {
            if (!ClientRegisteredVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'No ClientRegistered Vendor Found.',
                { returnError: true }
              );
            }

            const { id: ClientRegisteredVendorId } = ClientRegisteredVendor;

            this.Router.navigate([
              'admin',
              'run-vive',
              'client-vendor',
              CompanyVendorId,
              'client',
              CompanyId,
              'client-registered-vendor',
              ClientRegisteredVendorId,
              'action',
              'edit',
              'overview'
            ]);
            return of(true);
          })
        );
      })
    );

    concat(...observableArr)
      .pipe(takeWhile((result) => result === true))
      .subscribe();
  }

  archiveClientVendor() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot archive Client/Vendor relationship. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmArchiveVendorModalComponent,
      { backdrop: 'static', size: 'lg' }
    );
    Object.assign(componentInstance, { modelType: 'companyVendor' });

    from(result).subscribe({
      next: (note) => {
        if (note) {
          this.CompanyVendorService.saveCompanyVendor(
            Object.assign(this.CompanyVendor!, {
              isArchived: true,
              Notes: [note]
            })
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success!',
                'Successfully archived client vendor.'
              );
              this.cdr.markForCheck();
              this.Router.navigate(['admin', 'run-vive', { viveStatus: '' }]);
            }
          });
        }
      }
    });
  }
}
